/**
 * What a written character is reduced to before anything compares it.
 *
 * A stroke arrives as a run of points, from a reader's finger or from the
 * median line `hanzi-writer-data` records. Neither is comparable to the other
 * as it stands. A finger samples wherever the browser fires a pointer event,
 * the medians are sampled wherever the outline turns, and the two are drawn at
 * different sizes in different corners of whatever box they were drawn in.
 *
 * This module takes both to one description. A stroke becomes a handful of
 * straight runs, and each run holds the direction it goes in, how far it goes
 * and where in the character it sits. `handwriting-lookup.ts` compares two of these
 * and `handwriting-line.ts` writes one down.
 *
 * **Every figure is a whole number on a 64-step grid.** The published index is
 * quantised (a character is three characters of text per run), and the ink a
 * reader draws goes through the same quantisation before it is compared. So the
 * build and the browser describe a stroke identically, and a comparison is
 * arithmetic on small integers. Measured over 400 characters, the grid costs
 * nothing: 97.5% top-1 against 97.7% on full precision.
 */

import {
  type StrokeBounds,
  cornerTolerance,
  strokeBounds,
  strokeCorners,
} from "./stroke-corners.js";
import { strokeRun } from "./stroke-run.js";

/** The number of steps every figure in a description is measured in. */
export const shapeSteps = 64;

/** How many cells across the character is divided into for a run's position. */
export const placeSteps = 8;

/** One point of a stroke, in whatever space it was drawn in. */
export type StrokePoint = readonly [x: number, y: number];

/** One stroke, as the pad or the median data hands it over. */
export type StrokePoints = readonly StrokePoint[];

/**
 * One straight run of a stroke.
 *
 * A stroke of one run is a straight line. 横 and 竖 are one run each, 折 is
 * two, and the 竖弯钩 of 也 is three. Over the 9,276 characters the index
 * covers, 109,492 strokes come to 143,425 runs.
 */
export interface StrokeRun {
  /**
   * Which way the run goes, in 64 steps around the circle.
   *
   * Step 0 is due west and the count runs clockwise on screen (down the page
   * is the positive y direction, as it is in every coordinate space a browser
   * hands over). A 横 written left to right is step 32.
   */
  readonly direction: number;

  /**
   * How long the run is, in 64ths of the character's own span.
   *
   * The span is the longer side of the box the whole character was drawn in,
   * so a 一 on its own is one run of about 64 and the 点 of 冫 is about 8.
   */
  readonly length: number;

  /**
   * Where the middle of the run sits, as a cell of an 8 by 8 grid.
   *
   * The column times 8 plus the row, counting from the top left of the box the
   * character was drawn in.
   */
  readonly place: number;
}

/**
 * One stroke, as its runs and what is true of the whole of it.
 *
 * The runs are the shape and the three figures beside them are the stroke. A
 * comparison asks both, and `handwriting-lookup.ts` says why they are asked
 * separately.
 *
 * **They are derived rather than stored, and derived here rather than at every
 * comparison.** The published index holds runs, so both the ink and a line read
 * back off the file come through {@link strokeOf} and neither can drift from
 * the other. Computing them at every comparison instead cost 12 milliseconds a
 * lookup against under 2.
 */
export interface Stroke {
  /** The straight runs it is made of, in the order they were drawn. */
  readonly runs: readonly StrokeRun[];

  /**
   * How far it goes, over all its runs. Never nought, being a divisor.
   *
   * A share of the character's own span, on the same 64-step scale a run's
   * length is on, so a stroke of several runs can exceed 63.
   */
  readonly length: number;

  /** Which column of the 8 by 8 grid its middle falls in, as a fraction. */
  readonly column: number;

  /** Which row of it. */
  readonly row: number;
}

/** A whole character, as its strokes in writing order. */
export type CharacterShape = readonly Stroke[];

/**
 * A stroke, from the runs it is made of.
 *
 * The middle is the average of its runs' middles weighted by their lengths,
 * which for a stroke of one run is that run's own. A stroke's start and end are
 * not recorded, a run's middle being the one position the index holds, so this
 * is as near the middle of the stroke as the file can say.
 *
 * The weight is a run's length and one more. A run of no length is a 点 the
 * reader tapped, and its place is the whole of what that stroke has to say;
 * weighted by its length alone it would say nothing.
 */
export function strokeOf(runs: readonly StrokeRun[]): Stroke {
  let length = 0;
  let column = 0;
  let row = 0;
  let weights = 0;

  for (const run of runs) {
    const weight = run.length + 1;

    length += run.length;
    weights += weight;
    column += Math.floor(run.place / placeSteps) * weight;
    row += (run.place % placeSteps) * weight;
  }

  return {
    runs,
    length: Math.max(length, 1),
    column: column / Math.max(weights, 1),
    row: row / Math.max(weights, 1),
  };
}

/**
 * Describes a set of strokes, whoever drew them.
 *
 * The strokes are scaled into the unit square together, so the description says
 * where each stroke sits relative to the others and says nothing about how big
 * the whole character was or where on the pad it was written.
 *
 * **A frame given takes the place of the strokes' own bounds.** A lookup has
 * nothing but the ink, and the box the ink fills is the character. A drill
 * marks one stroke at a time against a character already laid out in the
 * square, and a lone 横 scaled to its own bounds would fill that square
 * wherever it was drawn. `stroke-step.ts` hands the square itself over, so
 * one stroke is described by where it sits in the character.
 *
 * **A corner tolerance given takes the place of {@link cornerTolerance}.** The
 * outlines write the 竖 of 十 with a lead-in at the top, which the measured
 * tolerance keeps as a run of its own, and a reader draws it straight. A
 * coarser description smooths the lead-in away, and `stroke-step.ts` costs
 * the ink against both.
 *
 * A stroke that came back as a single point (a 点 tapped rather than flicked,
 * or a median of one entry) keeps a run of zero length at the place it was
 * tapped. Dropping it would leave the character a stroke short of the one the
 * reader wrote.
 */
export function characterShape(
  strokes: readonly StrokePoints[],
  frame?: StrokeBounds,
  corner = cornerTolerance,
): CharacterShape {
  const drawn = strokes.filter((stroke) => stroke.length > 0);

  if (drawn.length === 0) {
    return [];
  }

  const { left, top, span } = frame ?? strokeBounds(drawn);
  const scaled = drawn.map((stroke) =>
    strokeCorners(stroke, span * corner).map(([x, y]): StrokePoint => [
      (x - left) / span,
      (y - top) / span,
    ]),
  );

  return scaled.map((stroke) => {
    const runs: StrokeRun[] = [];

    for (let index = 0; index + 1 < stroke.length; index += 1) {
      runs.push(
        strokeRun(
          stroke[index] as StrokePoint,
          stroke[index + 1] as StrokePoint,
        ),
      );
    }

    return strokeOf(
      runs.length > 0
        ? runs
        : [strokeRun(stroke[0] as StrokePoint, stroke[0] as StrokePoint)],
    );
  });
}
